import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL from "../config";
const VerifyBusiness = ({ companyId, onClose }) => {
    const [documents, setDocuments] = useState({});
    const [requiredDocs, setRequiredDocs] = useState([]);
    const [acknowledged, setAcknowledged] = useState(false);
    const [loading, setLoading] = useState(false);
    const [companyType, setCompanyType] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [companyTypes, setCompanyTypes] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCompanyDetails();
    }, []);

    const fetchCompanyDetails = async () => {
        try {
            console.log(`Fetching company details for ID: ${companyId}`);
            const response = await axios.get(`${API_BASE_URL}/companies/${companyId}`);
            console.log('Company response:', response.data);
            const fetchedCompanyType = response.data.company?.company_type || '';
            setCompanyType(fetchedCompanyType);

            const typesResponse = await axios.get(`${API_BASE_URL}/company-types`);
            console.log('Company types response:', typesResponse.data);
            setCompanyTypes(typesResponse.data || []);

            if (fetchedCompanyType) {
                fetchRequiredDocuments(fetchedCompanyType);
            }
        } catch (error) {
            console.error('Failed to fetch company details:', error);
            setError('Failed to load company details or company types.');
        }
    };

    const fetchRequiredDocuments = async (type) => {
        try {
            console.log(`Fetching document types for company type: ${type}`);
            const docsResponse = await axios.get(`${API_BASE_URL}/document-types?company_type=${encodeURIComponent(type)}`);
            console.log('Document types response:', docsResponse.data);
            setRequiredDocs(docsResponse.data || []);
        } catch (error) {
            console.error('Failed to fetch required documents:', error);
            setError('Failed to load required documents.');
        }
    };

    const handleCompanyTypeChange = async (e) => {
        const selectedType = e.target.value;
        setCompanyType(selectedType);
        if (selectedType) {
            setError(null);
            fetchRequiredDocuments(selectedType);
        } else {
            setRequiredDocs([]);
        }
    };

    const handleFileDrop = (e, docType) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
            setDocuments(prev => ({ ...prev, [docType]: file }));
            setError(null);
        } else {
            setError('Please upload a valid PDF, JPEG, or PNG file.');
        }
    };

    const handleFileSelect = (e, docType) => {
        const file = e.target.files[0];
        if (file && ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
            setDocuments(prev => ({ ...prev, [docType]: file }));
            setError(null);
        } else {
            setError('Please upload a valid PDF, JPEG, or PNG file.');
        }
    };

     const handleSubmit = async (e) => {
        e.preventDefault();
        if (!acknowledged) {
            setError('Please acknowledge responsibility for the uploaded documents.');
            return;
        }
        if (!companyType) {
            setError('Please select a company type.');
            return;
        }
        if (requiredDocs.some(doc => doc.is_mandatory && !documents[doc.document_name])) {
            setError('Please upload all mandatory documents.');
            return;
        }

        setLoading(true);
        setError(null);
        const formData = new FormData();
        formData.append('responsibility_acknowledged', '1');
        formData.append('company_type', companyType);

        Object.keys(documents).forEach((docType, index) => {
            formData.append(`documents[${index}]`, documents[docType]);
            formData.append(`document_types[${index}]`, docType);
        });

        try {
            await axios.post(`${API_BASE_URL}/companies/${companyId}/verification-documents`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setIsSubmitted(true);
            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Documents submitted and company type updated successfully!',
                confirmButtonText: 'OK',
                confirmButtonColor: '#007bff', // Bootstrap primary color
            });
        } catch (error) {
            console.error('Upload failed:', error);
            setError('Upload failed: ' + (error.response?.data?.error || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal fade show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5>Verify Business</h5>
                        <button onClick={onClose} className="btn-close"></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}
                        {!isSubmitted ? (
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label htmlFor="companyType" className="form-label">
                                        Company Type {companyType ? '' : '*'}
                                    </label>
                                    <select
                                        id="companyType"
                                        className="form-select"
                                        value={companyType}
                                        onChange={handleCompanyTypeChange}
                                        disabled={!!companyType || loading}
                                    >
                                        <option value="">Select Company Type</option>
                                        {companyTypes.map(type => (
                                            <option key={type.id} value={type.name}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {requiredDocs.length > 0 && (
                                    <div className="row">
                                        {requiredDocs.map(doc => (
                                            <div key={doc.document_name} className="col-md-6 mb-3">
                                                <label className="form-label">
                                                    {doc.document_name} {doc.is_mandatory ? '*' : ''}
                                                </label>
                                                <div
                                                    className="drop-zone border p-3 text-center"
                                                    onDrop={(e) => handleFileDrop(e, doc.document_name)}
                                                    onDragOver={(e) => e.preventDefault()}
                                                >
                                                    {documents[doc.document_name] ? (
                                                        <span className="text-success">
                                                            ✅ {documents[doc.document_name].name}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-cloud-upload-alt fa-2x mb-2"></i>
                                                            <p>
                                                                Drag & drop or{' '}
                                                                <input
                                                                    type="file"
                                                                    className="d-inline"
                                                                    onChange={(e) => handleFileSelect(e, doc.document_name)}
                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                    disabled={loading}
                                                                />
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="form-check mb-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="acknowledge"
                                        checked={acknowledged}
                                        onChange={(e) => setAcknowledged(e.target.checked)}
                                        disabled={loading}
                                    />
                                    <label className="form-check-label" htmlFor="acknowledge">
                                        <strong>
                                            I take full responsibility for the accuracy of uploaded documents. Any discrepancies may result in company delisting.
                                        </strong>
                                    </label>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading || !acknowledged || !companyType}
                                    >
                                        {loading ? 'Uploading...' : 'Submit for Verification'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div>
                                <p className="text-success">
                                    Documents have been submitted for verification. You can now view the status.
                                </p>
                                <div className="row">
                                    {Object.keys(documents).map(docType => (
                                        <div key={docType} className="col-md-6 mb-3">
                                            <label className="form-label">{docType}</label>
                                            <p>{documents[docType].name}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="modal-footer">
                                    <button onClick={onClose} className="btn btn-secondary">
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyBusiness;